import { useCallback, useEffect, useMemo, useState } from "react";
import * as admin from "../../api/admin";
import type { AdminUser, AuditEntry, CouponBatch, CouponCodeRow, CreditSummary, Invitation, Organization } from "../../api/types";
import { saveBlob } from "../../api/export";
import { editionText } from "../../lib/edition";
import { trialText } from "../../lib/dashboard";
import {
  INVITE_ROLES, auditDetail, auditLabel, bytesLabel, invitationNote, orgStatus, planPatch, planSummary, restrictionNote,
  roleChoices, seatsText, storageText, trialText as orgTrialText, userActions, userBadges,
} from "../../lib/admin";
import { couponCsv, lotExpiry, lotSource, signed, txnText } from "../../lib/credits";
import { creditsChanged } from "../../lib/credits";
import { useSession } from "../../state/session";
import { Dialog } from "../../ui/Dialog";
import { useDialogs } from "../../ui/dialogs";

type Tab = "users" | "invitations" | "credits" | "audit" | "orgs" | "coupons";
const cell = { padding: "6px 10px" } as const;
const head = { ...cell, fontWeight: 600, textAlign: "left", color: "var(--muted)" } as const;

/** The administration page: accounts, invitations, the audit log and (platform administrators) organizations. */
export function AdminPage() {
  const { user, isSuperAdmin } = useSession();
  const [tab, setTab] = useState<Tab>("users");
  const tabs: [Tab, string][] = [["users", "Users"], ["invitations", "Invitations"], ["credits", "Credits"], ["audit", "Audit log"],
    ...(isSuperAdmin ? [["orgs", "Organizations"] as [Tab, string], ["coupons", "Coupons"] as [Tab, string]] : [])];
  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }} data-testid="admin-page">
      <h2 style={{ margin: "0 0 12px" }}>Administration</h2>
      <div role="tablist" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {tabs.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} className={`btn${tab === id ? " primary" : ""}`}
            onClick={() => setTab(id)} data-testid={`tab-${id}`}>{label}</button>
        ))}
      </div>
      {user && tab === "users" && <UsersTab me={user} />}
      {user && tab === "invitations" && <InvitationsTab me={user} />}
      {tab === "credits" && <MyCreditsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "orgs" && <OrgsTab />}
      {tab === "coupons" && <CouponsTab />}
    </div>
  );
}

/** An organization's credits: what it has, where they came from and when they lapse, and what used them. */
function CreditsView({ s }: { s: CreditSummary }) {
  return (
    <div data-testid="credits-view">
      <div className="row" style={{ gap: 24, marginBottom: 12 }}>
        <div><div className="muted">Available</div><div style={{ fontSize: 22, fontWeight: 600 }} data-testid="credits-balance">{s.balance.toLocaleString("en-US")}</div></div>
        <div><div className="muted">Set aside for training in progress</div><div style={{ fontSize: 22 }}>{s.held.toLocaleString("en-US")}</div></div>
        <div><div className="muted">Monthly allowance</div><div style={{ fontSize: 22 }}>{s.monthly_allowance.toLocaleString("en-US")}</div></div>
      </div>
      <div className="muted" style={{ marginBottom: 6 }}>Credits pay for training, auto-annotation and model exports. This month's allowance is spent first, then whatever lapses soonest.</div>
      <h4 style={{ margin: "12px 0 6px" }}>Credits held</h4>
      <div className="list" style={{ padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="credits-lots">
          <thead><tr>{["From", "Left", "Received", "Lapses"].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>{s.lots.length ? s.lots.map((l) => (
            <tr key={l.id}><td style={cell}>{lotSource(l)}</td><td style={cell}>{l.remaining.toLocaleString("en-US")}</td>
              <td style={cell}>{l.granted.toLocaleString("en-US")} on {l.created_at.slice(0, 10)}</td><td style={cell}>{lotExpiry(l)}</td></tr>
          )) : <tr><td style={cell} colSpan={4} className="muted">None yet.</td></tr>}</tbody>
        </table>
      </div>
      <h4 style={{ margin: "12px 0 6px" }}>Recent activity</h4>
      <div className="list" style={{ padding: 0, maxHeight: 320, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="credits-ledger">
          <thead><tr>{["When", "What", "Credits", "Note"].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>{s.transactions.length ? s.transactions.map((t) => (
            <tr key={t.id}><td style={cell}>{t.created_at.slice(0, 16).replace("T", " ")}</td><td style={cell}>{txnText(t)}</td>
              <td style={{ ...cell, textAlign: "right", color: t.credits < 0 ? "var(--danger)" : undefined }}>{signed(t.credits)}</td><td style={cell}>{t.note}</td></tr>
          )) : <tr><td style={cell} colSpan={4} className="muted">Nothing yet.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

/** An organization admin types in a coupon code bought from InSiSo or a reseller. */
function RedeemBox({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await admin.redeemCode(code.trim());
      setResult({ ok: true, text: r.message }); setCode(""); onDone();
      creditsChanged();
    } catch (e) { setResult({ ok: false, text: (e as Error).message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="group" style={{ marginBottom: 12 }}>
      <span className="group-title">Redeem a code</span>
      <div className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
        <input className="input" style={{ flex: 1, fontFamily: "var(--font-mono, monospace)" }} placeholder="MB-XXXX-XXXX-XXXX" value={code}
          data-testid="redeem-code" onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void redeem(); }} />
        <button className="btn primary" disabled={busy || !code.trim()} onClick={redeem} data-testid="redeem-go">{busy ? "Redeeming…" : "Redeem"}</button>
      </div>
      {result && <div className={result.ok ? "muted" : "warning-text"} style={{ marginTop: 6 }} data-testid="redeem-result">{result.text}</div>}
    </div>
  );
}

function MyCreditsTab() {
  const [s, setS] = useState<CreditSummary | null>(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => { admin.myCredits().then((x) => { setS(x); setErr(""); }).catch((e) => setErr((e as Error).message)); }, []);
  useEffect(load, [load]);
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}><button className="btn" onClick={load}>Refresh</button></div>
      <RedeemBox onDone={load} />
      {s ? <CreditsView s={s} /> : <div className="muted">{err || "Loading…"}</div>}
      <div className="muted" style={{ marginTop: 10 }}>To buy credits or Pro, contact InSiSo or your reseller.</div>
    </>
  );
}

function CreditsDialog({ org, onClose, onDone }: { org: Organization; onClose: () => void; onDone: () => void }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const [s, setS] = useState<CreditSummary | null>(null);
  const [f, setF] = useState({ credits: "", days: "", note: "" });
  // One key per grant: a double click, or a retry after a network error, grants once;
  // a new key once it has landed, so the next grant from this dialog is a new grant.
  const newKey = () => `web-${org.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const [key, setKey] = useState(newKey);
  useEffect(() => { admin.organizationCredits(org.id).then(setS).catch(() => {}); }, [org.id]);
  const grant = async () => {
    const credits = Number(f.credits), days = f.days.trim() === "" ? undefined : Number(f.days);
    if (!(credits > 0) || !Number.isInteger(credits)) { await dlg.alert({ title: "Check the amount", text: "Credits must be a whole number above zero." }); return; }
    if (days !== undefined && !(Number.isInteger(days) && days >= 0)) { await dlg.alert({ title: "Check the days", text: "Days must be a whole number (0 = never lapse), or blank for the server default." }); return; }
    if (f.note.trim().length < 3) { await dlg.alert({ title: "Say why", text: "Give a note (a PO number, say); it is kept with the grant." }); return; }
    if (await attempt(async () => setS(await admin.grantCredits(org.id, { credits, valid_days: days, note: f.note.trim() }, key)))) {
      setKey(newKey());
      setF({ credits: "", days: "", note: "" });
      onDone();
    }
  };
  return (
    <Dialog title={`Credits — ${org.name}`} onClose={onClose} width={720} testId="org-credits-dialog">
      {s ? <CreditsView s={s} /> : <div className="muted">Loading…</div>}
      <h4 style={{ margin: "14px 0 6px" }}>Grant credits</h4>
      <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
        <div style={field}><label htmlFor="cg-n">Credits</label><input id="cg-n" className="input" style={{ width: 120 }} value={f.credits} data-testid="grant-credits" onChange={(e) => setF({ ...f, credits: e.target.value })} /></div>
        <div style={field}><label htmlFor="cg-d">Valid days (blank = default, 0 = never)</label><input id="cg-d" className="input" style={{ width: 120 }} value={f.days} data-testid="grant-days" onChange={(e) => setF({ ...f, days: e.target.value })} /></div>
        <div style={{ ...field, flex: 1 }}><label htmlFor="cg-note">Note (recorded)</label><input id="cg-note" className="input" placeholder="PO 4711" value={f.note} data-testid="grant-note" onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
      </div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Close</button><button className="btn primary" onClick={grant} data-testid="grant-save">Grant</button></div>
    </Dialog>
  );
}

function CouponsTab() {
  const attempt = useAttempt();
  const dlg = useDialogs();
  const [batches, setBatches] = useState<CouponBatch[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sub, setSub] = useState<null | "new" | "codes">(null);
  const [minted, setMinted] = useState<{ batch: CouponBatch; codes: string[] } | null>(null);
  const load = useCallback(async () => { setBatches(await admin.couponBatches()); }, []);
  useEffect(() => { void load().catch(() => {}); }, [load]);
  const selected = batches.find((b) => b.id === selectedId) ?? null;
  const withdrawAll = async () => {
    if (!selected) return;
    const reason = (await dlg.prompt({ title: "Withdraw unused codes", label: `Why are the ${selected.unused} unused code(s) of batch #${selected.id} being withdrawn? Redeemed ones keep what they gave.` }))?.trim();
    if (!reason) return;
    await attempt(() => admin.revokeCouponBatch(selected.id, reason), load);
  };
  const th = ["#", "Created", "Reseller", "Reference", "Each code gives", "Redeemed", "Withdrawn", "Codes expire"];
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button className="btn primary" onClick={() => setSub("new")} data-testid="coupon-new">New batch…</button>
        <button className="btn" disabled={!selected} onClick={() => setSub("codes")} data-testid="coupon-codes">Codes…</button>
        <button className="btn danger" disabled={!selected || !selected.unused} onClick={withdrawAll} data-testid="coupon-withdraw-all">Withdraw unused…</button>
      </div>
      <div className="list" style={{ padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="coupon-table">
          <thead><tr>{th.map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>{batches.length ? batches.map((b) => (
            <tr key={b.id} aria-selected={b.id === selectedId} onClick={() => setSelectedId(b.id)} data-testid={`coupon-batch-${b.id}`}
              style={{ cursor: "pointer", background: b.id === selectedId ? "var(--selection)" : undefined, color: b.id === selectedId ? "#fff" : undefined }}>
              <td style={cell}>{b.id}</td><td style={cell}>{b.created_at.slice(0, 10)}</td><td style={cell}>{b.reseller || "—"}</td>
              <td style={cell}>{b.reference || "—"}</td><td style={cell}>{b.gives}</td>
              <td style={cell}>{b.redeemed} / {b.size}</td><td style={cell}>{b.revoked}</td>
              <td style={cell}>{b.codes_expire_at ? b.codes_expire_at.slice(0, 10) : "never"}</td>
            </tr>
          )) : <tr><td style={cell} colSpan={th.length} className="muted">No coupons yet.</td></tr>}</tbody>
        </table>
      </div>
      {sub === "new" && <NewBatchDialog onClose={() => setSub(null)} onMinted={(m) => { setSub(null); setMinted(m); setSelectedId(m.batch.id); void load(); }} />}
      {minted && <MintedDialog minted={minted} onClose={() => setMinted(null)} />}
      {sub === "codes" && selected && <BatchCodesDialog batchId={selected.id} onClose={() => { setSub(null); void load(); }} />}
    </>
  );
}

function NewBatchDialog({ onClose, onMinted }: { onClose: () => void; onMinted: (m: { batch: CouponBatch; codes: string[] }) => void }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const [f, setF] = useState({ count: "10", proDays: "365", credits: "", creditDays: "365", codeDays: "365", reseller: "", reference: "", note: "" });
  const num = (v: string) => (v.trim() === "" ? 0 : Number(v));
  const submit = async () => {
    const count = num(f.count), proDays = num(f.proDays), credits = num(f.credits);
    const bad = [count, proDays, credits, num(f.creditDays), num(f.codeDays)].some((n) => !Number.isInteger(n) || n < 0);
    if (bad || count < 1) { await dlg.alert({ title: "Check the numbers", text: "Use whole numbers: at least 1 code; days and credits 0 or more." }); return; }
    if (!proDays && !credits) { await dlg.alert({ title: "Nothing to give", text: "Each code must give Pro (days), credits, or both." }); return; }
    await attempt(async () => onMinted(await admin.mintCoupons({
      count, edition: proDays ? "pro" : null, edition_days: proDays, credits, credit_valid_days: num(f.creditDays),
      code_valid_days: num(f.codeDays), reseller: f.reseller.trim(), reference: f.reference.trim(), note: f.note.trim(),
    })));
  };
  const input = (id: keyof typeof f, label: string, placeholder = "") => (
    <div style={field}><label htmlFor={`cb-${id}`}>{label}</label>
      <input id={`cb-${id}`} className="input" placeholder={placeholder} value={f[id]} data-testid={`cb-${id}`} onChange={(e) => setF({ ...f, [id]: e.target.value })} /></div>);
  return (
    <Dialog title="New Coupon Batch" onClose={onClose} width={460} testId="coupon-new-dialog">
      {input("count", "How many codes (1–1000)")}
      {input("proDays", "Each gives Pro for this many days (blank = no Pro, credits only)")}
      {input("credits", "Each gives this many credits (blank = none)")}
      {input("creditDays", "Those credits last (days; 0 = never lapse)")}
      {input("codeDays", "An unused code stops working after (days; 0 = never)")}
      {input("reseller", "Reseller", "Bosch Rexroth")}
      {input("reference", "Purchase order / reference", "PO 4711")}
      {input("note", "Note (kept with the batch)")}
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} data-testid="cb-create">Create codes</button></div>
    </Dialog>
  );
}

/** The new codes, shown this once: they are not stored anywhere readable. */
function MintedDialog({ minted, onClose }: { minted: { batch: CouponBatch; codes: string[] }; onClose: () => void }) {
  const dlg = useDialogs();
  const [saved, setSaved] = useState(false);
  const { batch, codes } = minted;
  const save = () => {
    const name = `coupons_${batch.id}${batch.reference ? "_" + batch.reference.replace(/[^A-Za-z0-9-]+/g, "_") : ""}.csv`;
    saveBlob(new Blob([couponCsv(batch, codes)], { type: "text/csv" }), name);
    setSaved(true);
  };
  const close = async () => {
    if (!saved && !(await dlg.confirm({ title: "Codes not saved", text: "These codes are shown only once. Close without downloading the CSV?", yes: "Close anyway", no: "Go back", danger: true }))) return;
    onClose();
  };
  return (
    <Dialog title={`Batch #${batch.id}: ${codes.length} code(s)`} onClose={close} width={520} testId="coupon-minted-dialog">
      <div className="warning-text" style={{ marginBottom: 8 }}>Save these now: they are shown only once and are not stored anywhere they can be read again.</div>
      <div className="muted" style={{ marginBottom: 6 }}>Each gives {batch.gives}.{batch.codes_expire_at ? ` Unused codes stop working on ${batch.codes_expire_at.slice(0, 10)}.` : ""}</div>
      <textarea className="input" readOnly rows={Math.min(12, codes.length + 1)} style={{ width: "100%", fontFamily: "var(--font-mono, monospace)" }}
        value={codes.join("\n")} data-testid="minted-codes" />
      <div className="dialog-actions"><button className="btn" onClick={close}>Close</button>
        <button className="btn primary" onClick={save} data-testid="minted-csv">{saved ? "Downloaded ✓" : "Download CSV"}</button></div>
    </Dialog>
  );
}

function BatchCodesDialog({ batchId, onClose }: { batchId: number; onClose: () => void }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const [d, setD] = useState<{ batch: CouponBatch; codes: CouponCodeRow[] } | null>(null);
  useEffect(() => { admin.couponBatch(batchId).then(setD).catch(() => {}); }, [batchId]);
  const withdraw = async () => {
    const code = (await dlg.prompt({ title: "Withdraw a code", label: "The full code to withdraw (MB-…). Only an unused code can be withdrawn." }))?.trim();
    if (!code) return;
    const reason = (await dlg.prompt({ title: "Withdraw a code", label: "Why? (recorded)" }))?.trim();
    if (!reason) return;
    await attempt(async () => setD(await admin.revokeCoupon(code, reason)));
  };
  return (
    <Dialog title={`Batch #${batchId}${d ? ` — ${d.batch.gives}` : ""}`} onClose={onClose} width={640} testId="coupon-codes-dialog">
      {d && <div className="muted" style={{ marginBottom: 8 }}>{d.batch.reseller || "No reseller"} · {d.batch.reference || "no reference"} · {d.batch.redeemed} of {d.batch.size} redeemed, {d.batch.revoked} withdrawn</div>}
      <div className="list" style={{ padding: 0, maxHeight: 360, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="coupon-code-table">
          <thead><tr>{["Code", "Status", "By", "When", "Why withdrawn"].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>{(d?.codes ?? []).map((c) => (
            <tr key={c.id}><td style={{ ...cell, fontFamily: "var(--font-mono, monospace)" }}>…{c.hint}</td><td style={cell}>{c.status}</td>
              <td style={cell}>{c.redeemed_org ?? ""}</td><td style={cell}>{(c.redeemed_at ?? c.revoked_at ?? "").slice(0, 10)}</td><td style={cell}>{c.revoked_reason}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <div className="dialog-actions"><button className="btn danger" onClick={withdraw} data-testid="coupon-withdraw-one">Withdraw a code…</button><button className="btn" onClick={onClose}>Close</button></div>
    </Dialog>
  );
}

function useAttempt() {
  const dlg = useDialogs();
  return useCallback(async (fn: () => Promise<unknown>, after?: () => Promise<void> | void) => {
    try { await fn(); await after?.(); return true; }
    catch (e) { await dlg.alert({ title: "Error", text: (e as Error).message }); return false; }
  }, [dlg]);
}

function useOrgs(enabled: boolean) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  useEffect(() => { if (enabled) admin.listOrganizations().then(setOrgs).catch(() => {}); }, [enabled]);
  return orgs;
}

function UsersTab({ me }: { me: AdminUser }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const orgs = useOrgs(me.role === "super_admin");
  const orgName = (id?: number | null) => orgs.find((o) => o.id === id)?.name ?? (id ?? "");
  const selected = users.find((u) => u.id === selectedId) ?? null;
  const can = userActions(selected, me);
  const load = useCallback(async () => { await attempt(async () => setUsers(await admin.listUsers())); }, [attempt]);
  useEffect(() => { void load(); }, [load]);
  const [mine, setMine] = useState<Organization | null>(null);
  useEffect(() => { admin.myOrganization().then(setMine).catch(() => {}); }, [users.length]);

  const changeRole = async (role: string) => {
    if (!selected || role === selected.role) return;
    if (await dlg.confirm({ title: "Change Role", text: `Set '${selected.username}' as ${role}?` })) {
      await attempt(() => admin.setRole(selected.id, role), load);
    }
  };
  const toggleActive = async () => {
    if (!selected) return;
    const off = selected.is_active !== false;
    if (!off) {
      if (await dlg.confirm({ title: "Enable Account", text: `Enable '${selected.username}'?` })) await attempt(() => admin.setActive(selected.id, true), load);
      return;
    }
    const reason = (await dlg.prompt({
      title: "Disable Account", label: `Why is '${selected.username}' being disabled? They are signed out at once and cannot sign in. The reason is recorded in the audit log.`,
    }))?.trim();
    if (reason === undefined || reason === null) return;
    if (reason.length < 3) { await dlg.alert({ title: "Reason needed", text: "Give a reason of at least 3 characters." }); return; }
    await attempt(() => admin.setActive(selected.id, false, reason), load);
  };
  const decide = async (approve: boolean) => {
    if (selected && await dlg.confirm({ title: approve ? "Approve" : "Reject", text: `${approve ? "Approve" : "Reject"} the sign-up of '${selected.username}'?` })) {
      await attempt(() => admin.decide(selected.id, approve), load);
    }
  };

  return (
    <>
      {mine && <div className="muted" style={{ marginBottom: 8 }} data-testid="plan-summary">{mine.name}: {planSummary(mine)}</div>}
      <div className="list" style={{ padding: 0, maxHeight: 420 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="user-table">
          <thead><tr>
            {["Username", "Email", "Role", ...(me.role === "super_admin" ? ["Organization"] : []), "Status", "Trial", "Last sign-in"].map((h) => <th key={h} style={head}>{h}</th>)}
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} aria-selected={u.id === selectedId} data-testid={`user-${u.username}`} onClick={() => setSelectedId(u.id)}
                style={{ cursor: "pointer", background: u.id === selectedId ? "var(--selection)" : undefined, color: u.id === selectedId ? "#fff" : undefined,
                  opacity: u.is_active === false ? 0.6 : 1 }}>
                <td style={cell}>{u.username}</td><td style={cell}>{u.email}</td><td style={cell}>{u.role}</td>
                {me.role === "super_admin" && <td style={cell}>{orgName(u.org_id)}</td>}
                <td style={cell} data-testid={`status-${u.username}`}>{userBadges(u).join(", ") || "Active"}</td>
                <td style={cell}>{trialText(u)}</td>
                <td style={cell}>{u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && restrictionNote(selected) && (
        <div className="muted" role="note" style={{ marginTop: 6 }} data-testid="restriction-note">{restrictionNote(selected)}</div>
      )}
      <div className="dialog-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap", alignItems: "center" }}>
        <label htmlFor="role-pick" className="muted">Role</label>
        <select id="role-pick" className="select" disabled={!can.role} value={selected?.role ?? ""} data-testid="role-pick"
          onChange={(e) => void changeRole(e.target.value)}>
          {!selected && <option value="" />}
          {[...new Set([selected?.role ?? "", ...roleChoices(me)])].filter(Boolean).map((r) => <option key={r}>{r}</option>)}
        </select>
        <button className="btn" disabled={!can.disable && !can.enable} onClick={toggleActive} data-testid="toggle-active">
          {selected && selected.is_active === false ? "Enable" : "Disable"}
        </button>
        <button className="btn primary" disabled={!can.approve} onClick={() => decide(true)} data-testid="approve">Approve</button>
        <button className="btn" disabled={!can.reject} onClick={() => decide(false)} data-testid="reject">Reject</button>
        <button className="btn" disabled={!selected} data-testid="extend-trial"
          onClick={() => selected && attempt(() => admin.extendTrial(selected.id, 7), load)}>Extend trial +7 days</button>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={load}>Refresh</button>
      </div>
    </>
  );
}

function InvitationsTab({ me }: { me: AdminUser }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const platform = me.role === "super_admin";
  const orgs = useOrgs(platform);
  const [rows, setRows] = useState<Invitation[]>([]);
  const [showClosed, setShowClosed] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("annotator");
  const [orgId, setOrgId] = useState<number | undefined>(undefined);
  const load = useCallback(async () => { await attempt(async () => setRows(await admin.listInvitations(showClosed))); }, [attempt, showClosed]);
  useEffect(() => { void load(); }, [load]);

  const send = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { await dlg.alert({ title: "Invalid email", text: "Enter the email address to invite." }); return; }
    if (await attempt(() => admin.createInvitation(email.trim(), role, orgId), load)) setEmail("");
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <input className="input" style={{ minWidth: 260 }} placeholder="Email address to invite" value={email} data-testid="invite-email"
          onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void send(); }} />
        <select className="select" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role" aria-label="Role">
          {INVITE_ROLES.map((r) => <option key={r}>{r}</option>)}
        </select>
        {platform && (
          <select className="select" value={orgId ?? ""} aria-label="Organization" data-testid="invite-org"
            onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">My organization</option>
            {orgs.filter((o) => o.id !== me.org_id).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
        <button className="btn primary" onClick={send} data-testid="invite-send">Send invitation</button>
        <span style={{ flex: 1 }} />
        <label className="muted"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show used, withdrawn and expired</label>
      </div>
      <div className="list" style={{ padding: 0, maxHeight: 420 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="invite-table">
          <thead><tr>{["Email", "Role", "Invited by", "Status", ""].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td style={cell} colSpan={5} className="muted">No invitations.</td></tr>}
            {rows.map((i) => (
              <tr key={i.id} data-testid={`invite-${i.email}`}>
                <td style={cell}>{i.email}</td><td style={cell}>{i.role}</td><td style={cell}>{i.invited_by ?? "—"}</td>
                <td style={cell}>{invitationNote(i)}</td>
                <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                  {(i.status === "pending" || i.status === "expired") && (
                    <button className="btn" onClick={() => attempt(() => admin.resendInvitation(i.id), load)}>Resend</button>)}{" "}
                  {i.status === "pending" && (
                    <button className="btn" onClick={() => attempt(() => admin.revokeInvitation(i.id), load)}>Withdraw</button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const FILTERS: [string, string][] = [["", "All events"], ["user.", "Accounts"], ["invite.", "Invitations"], ["org.", "Organizations"]];

function AuditTab() {
  const attempt = useAttempt();
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [filter, setFilter] = useState("");
  const [more, setMore] = useState(false);
  const PAGE = 50;
  const load = useCallback(async (beforeId?: number) => {
    await attempt(async () => {
      const page = await admin.auditLog({ limit: PAGE, before_id: beforeId, action: filter || undefined });
      setRows((prev) => (beforeId ? [...prev, ...page] : page));
      setMore(page.length === PAGE);
    });
  }, [attempt, filter]);
  useEffect(() => { void load(); }, [load]);
  const last = useMemo(() => rows[rows.length - 1]?.id, [rows]);

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <select className="select" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter" data-testid="audit-filter">
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className="btn" onClick={() => load()}>Refresh</button>
      </div>
      <div className="list" style={{ padding: 0, maxHeight: 460 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="audit-table">
          <thead><tr>{["When", "Who", "What", "Whom", "Details", "From"].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td style={cell} colSpan={6} className="muted">Nothing recorded yet.</td></tr>}
            {rows.map((e) => (
              <tr key={e.id} data-testid={`audit-${e.id}`}>
                <td style={cell}>{e.created_at.slice(0, 16).replace("T", " ")}</td>
                <td style={cell}>{e.actor_name || "—"}</td><td style={cell}>{auditLabel(e.action)}</td>
                <td style={cell}>{e.target_label}</td><td style={cell} className="muted">{auditDetail(e)}</td>
                <td style={cell} className="muted">{e.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more && <div style={{ marginTop: 8 }}><button className="btn" onClick={() => load(last)} data-testid="audit-more">Load older entries</button></div>}
    </>
  );
}

function OrgsTab() {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const { user: me } = useSession();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sub, setSub] = useState<null | "create" | "plan" | "credits" | "purge">(null);
  const [busy, setBusy] = useState("");
  const selected = orgs.find((o) => o.id === selectedId) ?? null;
  const load = useCallback(async () => { await attempt(async () => setOrgs(await admin.listOrganizations())); }, [attempt]);
  useEffect(() => { void load(); }, [load]);

  const suspend = async () => {
    if (!selected) return;
    const reason = (await dlg.prompt({
      title: "Suspend Organization",
      label: `Why is '${selected.name}' being suspended? Everyone in it is signed out and locked out, and its training stops. Nothing is deleted. The reason is recorded in the audit log.`,
    }))?.trim();
    if (reason === undefined || reason === null) return;
    if (reason.length < 3) { await dlg.alert({ title: "Reason needed", text: "Give a reason of at least 3 characters." }); return; }
    await attempt(() => admin.suspendOrganization(selected.id, reason), load);
  };
  const unsuspend = async () => {
    if (selected && await dlg.confirm({ title: "Reinstate Organization", text: `Let the people in '${selected.name}' sign in again?` })) {
      await attempt(() => admin.unsuspendOrganization(selected.id), load);
    }
  };
  const exportData = async () => {
    if (!selected) return;
    if (!(await dlg.confirm({ title: "Export Data", text: `Download all of '${selected.name}' data (images, annotations, models, users, audit trail) as one zip?\n\nThe download is built in your browser's memory, so for an organization with many gigabytes use the server command instead: server_admin.py export-org. This is recorded in the audit log.` }))) return;
    setBusy("Preparing the export…");
    await attempt(async () => saveBlob(await admin.exportOrganization(selected.id), `${selected.name.replace(/[^\w. -]+/g, "_")}_export.zip`));
    setBusy("");
  };
  const locked = !!selected && !!selected.suspended_at;
  const th = ["Organization", "Edition", "Users", "Trial", "Storage", "Credits", "Status"];
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
        <button className="btn primary" onClick={() => setSub("create")} data-testid="org-new">New organization…</button>
        <span className="muted" data-testid="org-busy">{busy}</span>
      </div>
      <div className="list" style={{ padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="org-table">
          <thead><tr>{th.map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
          <tbody>{orgs.map((o) => (
            <tr key={o.id} aria-selected={o.id === selectedId} data-testid={`org-${o.name}`} onClick={() => setSelectedId(o.id)}
              style={{ cursor: "pointer", background: o.id === selectedId ? "var(--selection)" : undefined, color: o.id === selectedId ? "#fff" : undefined }}>
              <td style={cell}>{o.name}</td>
              <td style={cell} data-testid={`org-edition-${o.name}`}>{editionText(o)}</td>
              <td style={cell} data-testid={`org-seats-${o.name}`}>{seatsText(o)}</td>
              <td style={cell} data-testid={`org-trial-${o.name}`}>{orgTrialText(o)}</td>
              <td style={cell} data-testid={`org-storage-${o.name}`}>{storageText(o)}</td>
              <td style={cell} data-testid={`org-credits-${o.name}`}>{(o.credit_balance ?? 0).toLocaleString("en-US")}</td>
              <td style={cell} data-testid={`org-status-${o.name}`}>{orgStatus(o)}</td></tr>))}</tbody>
        </table>
      </div>
      <div className="dialog-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
        <button className="btn" disabled={!selected} onClick={() => setSub("plan")} data-testid="org-plan">Edition, trial, seats &amp; storage…</button>
        <button className="btn" disabled={!selected} onClick={() => setSub("credits")} data-testid="org-credits">Credits…</button>
        <button className="btn" disabled={!selected} onClick={exportData} data-testid="org-export">Export data…</button>
        <button className="btn danger" disabled={!selected || !!selected.suspended_at || selected.id === me?.org_id} onClick={suspend} data-testid="org-suspend">Suspend…</button>
        <button className="btn" disabled={!selected || !selected.suspended_at} onClick={unsuspend} data-testid="org-unsuspend">Reinstate</button>
        <button className="btn danger" disabled={!selected || !locked || selected.id === me?.org_id} onClick={() => setSub("purge")} data-testid="org-purge"
          title="Erase all of this organization's data. Only for an organization that is already suspended.">Erase data…</button>
      </div>
      {sub === "create" && <CreateOrg onClose={() => setSub(null)} onDone={() => { setSub(null); void load(); }} />}
      {sub === "credits" && selected && <CreditsDialog org={selected} onClose={() => { setSub(null); void load(); }} onDone={() => void load()} />}
      {sub === "plan" && selected && <PlanDialog org={selected} onClose={() => setSub(null)} onDone={() => { setSub(null); void load(); }} />}
      {sub === "purge" && selected && <PurgeDialog org={selected} onClose={() => setSub(null)} onDone={() => { setSub(null); void load(); }} />}
    </>
  );
}

const field = { display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 } as const;

function CreateOrg({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const [f, setF] = useState({ name: "", trialDays: "10", maxUsers: "", storageGb: "", edition: "standard" as admin.Edition });
  const submit = async () => {
    if (!f.name.trim()) { await dlg.alert({ title: "Name needed", text: "Enter the organization's name." }); return; }
    const terms: admin.OrgTerms = {};
    if (f.trialDays.trim()) terms.trial_days = Number(f.trialDays);
    if (f.maxUsers.trim()) terms.max_users = Number(f.maxUsers);
    if (f.storageGb.trim()) terms.storage_cap_gb = Number(f.storageGb);
    if (Object.values(terms).some((v) => !(v > 0))) { await dlg.alert({ title: "Check the terms", text: "Days, users and GB must be numbers above zero, or blank for no limit." }); return; }
    // A trial is Standard underneath: Pro is set once they have paid.
    if (await attempt(() => admin.createOrganization(f.name.trim(), { ...terms, edition: terms.trial_days ? "standard" : f.edition }))) onDone();
  };
  return (
    <Dialog title="New Organization" onClose={onClose} width={420} testId="org-create-dialog">
      <div style={field}><label htmlFor="oc-name">Name</label><input id="oc-name" className="input" value={f.name} data-testid="org-name" onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
      <EditionField id="oc-edition" value={f.trialDays.trim() ? "standard" : f.edition} testId="org-edition" onTrial={!!f.trialDays.trim()} onChange={(edition) => setF({ ...f, edition })} />
      <div style={field}><label htmlFor="oc-days">Trial length (days; blank = no trial)</label><input id="oc-days" className="input" value={f.trialDays} data-testid="org-trial-days" onChange={(e) => setF({ ...f, trialDays: e.target.value })} /></div>
      <div style={field}><label htmlFor="oc-users">User limit (blank = unlimited)</label><input id="oc-users" className="input" value={f.maxUsers} data-testid="org-max-users" onChange={(e) => setF({ ...f, maxUsers: e.target.value })} /></div>
      <div style={field}><label htmlFor="oc-gb">Storage cap, GB (blank = server default)</label><input id="oc-gb" className="input" value={f.storageGb} data-testid="org-storage-gb" onChange={(e) => setF({ ...f, storageGb: e.target.value })} /></div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} data-testid="org-create">Create</button></div>
    </Dialog>
  );
}

function PlanDialog({ org, onClose, onDone }: { org: Organization; onClose: () => void; onDone: () => void }) {
  const dlg = useDialogs();
  const attempt = useAttempt();
  const [f, setF] = useState({
    trialEnds: org.trial_ends_at?.slice(0, 10) ?? "", maxUsers: org.max_users ? String(org.max_users) : "",
    storageGb: org.storage_cap_bytes ? String(+(org.storage_cap_bytes / 2 ** 30).toFixed(2)) : "",
    edition: (org.edition ?? "standard") as admin.Edition,
    editionEnds: org.edition_ends_at?.slice(0, 10) ?? "",
  });
  const submit = async () => {
    const { patch, error } = planPatch(org, f);
    if (error) { await dlg.alert({ title: "Check the terms", text: error }); return; }
    if (Object.keys(patch).length === 0) { onClose(); return; }
    if (await attempt(() => admin.setOrganizationPlan(org.id, patch))) onDone();
  };
  return (
    <Dialog title={`Edition, trial, seats & storage — ${org.name}`} onClose={onClose} width={440} testId="org-plan-dialog">
      <div className="muted" style={{ marginBottom: 10 }}>In use now: {seatsText(org)} users · {storageText(org)}. Blank removes a limit. Lowering one never deletes anything.</div>
      <EditionField id="op-edition" value={f.edition} testId="plan-edition" onChange={(edition) => setF({ ...f, edition, ...(edition === "pro" ? { trialEnds: "" } : {}) })} />
      {f.edition === "pro" && <div style={field}><label htmlFor="op-pro-until">Pro until (YYYY-MM-DD; blank = no end). A coupon sets this; after it the organization is Standard.</label>
        <input id="op-pro-until" className="input" placeholder="2027-10-10" value={f.editionEnds} data-testid="plan-pro-until" onChange={(e) => setF({ ...f, editionEnds: e.target.value })} /></div>}
      <div style={field}><label htmlFor="op-trial">Trial ends (YYYY-MM-DD; blank = no trial). Until this date the organization gets Pro; after it, Standard. Setting Pro above ends the trial.</label>
        <input id="op-trial" className="input" placeholder="2026-10-31" value={f.trialEnds} data-testid="plan-trial" onChange={(e) => setF({ ...f, trialEnds: e.target.value })} /></div>
      <div style={field}><label htmlFor="op-users">User limit</label><input id="op-users" className="input" value={f.maxUsers} data-testid="plan-users" onChange={(e) => setF({ ...f, maxUsers: e.target.value })} /></div>
      <div style={field}><label htmlFor="op-gb">Storage cap, GB (server default if blank; images only)</label><input id="op-gb" className="input" value={f.storageGb} data-testid="plan-gb" onChange={(e) => setF({ ...f, storageGb: e.target.value })} /></div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} data-testid="plan-save">Save</button></div>
    </Dialog>
  );
}

/** Standard or Pro. A trial gets Pro anyway; the edition is what applies once it ends. */
function EditionField({ id, value, testId, onChange, onTrial = false }: { id: string; value: admin.Edition; testId: string; onChange: (e: admin.Edition) => void; onTrial?: boolean }) {
  return (
    <div style={field}><label htmlFor={id}>{onTrial
      ? "Edition: Standard. During the trial the organization gets everything Pro has; afterwards it is Standard until it pays for Pro."
      : "Edition (what they have paid for). Pro adds Hailo, NVIDIA GPU export, measurement and bulk review. Choosing Pro ends any trial."}</label>
      <select id={id} className="select" value={value} disabled={onTrial} data-testid={testId} onChange={(e) => onChange(e.target.value as admin.Edition)}>
        <option value="standard">Standard</option>
        <option value="pro">Pro</option>
      </select></div>
  );
}

function PurgeDialog({ org, onClose, onDone }: { org: Organization; onClose: () => void; onDone: () => void }) {
  const dlg = useDialogs();
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [accounts, setAccounts] = useState(false);
  const [busy, setBusy] = useState(false);
  const ok = name === org.name && reason.trim().length >= 3 && !busy;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await admin.purgeOrganization(org.id, { confirm_name: name, reason: reason.trim(), delete_accounts: accounts });
      await dlg.alert({ title: "Data erased", text: `Removed ${r.projects} project(s), ${r.images} image(s), ${bytesLabel(r.bytes)}${accounts ? `, ${r.accounts_deleted} account(s)` : ""}.` });
      onDone();
    } catch (e) { await dlg.alert({ title: "Could not erase", text: (e as Error).message }); setBusy(false); }
  };
  return (
    <Dialog title={`Erase data — ${org.name}`} onClose={onClose} width={480} testId="org-purge-dialog">
      <p style={{ marginTop: 0 }}><b>This permanently deletes every project, image, annotation and model of this organization.</b> It cannot be undone.
        Export it first if it is to be handed back. The organization, its audit history and (unless ticked) its accounts stay.</p>
      <div style={field}><label htmlFor="pg-reason">Why</label><input id="pg-reason" className="input" value={reason} data-testid="purge-reason" onChange={(e) => setReason(e.target.value)} /></div>
      <div style={field}><label htmlFor="pg-name">Type the organization's name to confirm: <b>{org.name}</b></label>
        <input id="pg-name" className="input" value={name} data-testid="purge-name" onChange={(e) => setName(e.target.value)} /></div>
      <label className="muted"><input type="checkbox" checked={accounts} data-testid="purge-accounts" onChange={(e) => setAccounts(e.target.checked)} /> Also delete its user accounts</label>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn danger" disabled={!ok} onClick={submit} data-testid="purge-go">Erase permanently</button></div>
    </Dialog>
  );
}
